import { XIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { useEditor } from '@tiptap/react';
import { useEffect, useRef } from 'react';
import { sharedEditorExtensions } from '../TipTapCustomExtensions/sharedEditorExtensions';
import BlockHandle from './ColumnBlockHandle';
import {
  EditorContainer,
  EditorWrapper,
  StyledEditorContent,
} from './editorStyles';
import FixedToolbar from './FixedToolbar';
import { generateStimulusSheetContent } from './stimulusSheetContentGenerators';

interface WorksheetEditorProps {
  content: string;
  worksheetContent: string;
  onWorksheetContentChange: (content: string) => void;
  outOfSync: boolean;
  onRegenerate: () => void;
}

export default function WorksheetEditor({
  content,
  worksheetContent,
  onWorksheetContentChange,
  outOfSync,
  onRegenerate,
}: WorksheetEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const syncedContentRef = useRef(content);
  const onChangeRef = useRef(onWorksheetContentChange);
  onChangeRef.current = onWorksheetContentChange;

  const initialContent =
    worksheetContent || generateStimulusSheetContent(content);

  const editor = useEditor({
    extensions: [...sharedEditorExtensions],
    content: initialContent,
    onUpdate: ({ editor }) => {
      onChangeRef.current(editor.getHTML());
    },
  });

  // Auto-sync from lesson plan only when there's no saved worksheet content
  useEffect(() => {
    if (!editor || !content || worksheetContent) return;
    if (content !== syncedContentRef.current) {
      const generated = generateStimulusSheetContent(content);
      editor.commands.setContent(generated);
      syncedContentRef.current = content;
    }
  }, [editor, content, worksheetContent]);

  // On first mount with no saved worksheet content, push the auto-generated
  // content to parent state so that subsequent lesson plan edits trigger the
  // outOfSync warning rather than silently regenerating.
  useEffect(() => {
    if (!editor || worksheetContent) return;
    onChangeRef.current(editor.getHTML());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) {
    return null;
  }

  function handleRegenerate() {
    const generated = generateStimulusSheetContent(content);
    editor!.commands.setContent(generated);
    onWorksheetContentChange(generated);
    onRegenerate();
  }

  const handlePrint = () => {
    if (!editorRef.current) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      return;
    }

    // Copy stylesheets so editor content renders correctly
    const stylesheets = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]'),
    );
    const stylesHTML = stylesheets.map((s) => s.outerHTML).join('\n');

    doc.open();
    doc.write(`
      <html>
        <head>
          ${stylesHTML}
          <style>@page { margin: 1.5cm; }</style>
        </head>
        <body>${editorRef.current.outerHTML}</body>
      </html>
    `);
    doc.close();

    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();

    // Clean up after a short delay to allow print dialog to open
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };

  return (
    <EditorContainer>
      <FixedToolbar editor={editor} onExportToPdf={handlePrint} />
      {outOfSync && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-800">
          <span>
            The lesson plan has changed since this worksheet was last generated.
          </span>
          <div className="flex shrink-0 items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleRegenerate}
                  className="font-medium underline underline-offset-2 hover:text-amber-900"
                >
                  Regenerate worksheet
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <strong>Note:</strong> This will overwrite any changes you've
                made to the worksheet
              </TooltipContent>
            </Tooltip>
            <button
              onClick={onRegenerate}
              className="hover:text-amber-900"
              aria-label="Dismiss"
            >
              <XIcon />
            </button>
          </div>
        </div>
      )}
      <BlockHandle editor={editor} />
      <EditorWrapper className="EditorWrapper">
        <StyledEditorContent editor={editor} ref={editorRef} $worksheet />
      </EditorWrapper>
    </EditorContainer>
  );
}
